import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { usePermissions } from '../composables/usePermissions'
import {
  expireAdminSession,
  fetchPanelProfile,
  getHttpStatusCode,
  isAdminAuthenticated,
} from '../services/repodepot-api'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'system/dashboard',
        alias: '/dashboard',
        component: () => import('../pages/repodepot/DashboardPage.vue'),
        meta: { requiredPermissions: ['dashboard.view'] },
      },
      {
        name: 'settings',
        path: 'settings',
        redirect: { name: 'profile' },
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { viewportLocked: true },
      },
      {
        name: 'preferences-legacy',
        path: 'preferences',
        redirect: (to) => ({ name: 'profile', query: to.query, hash: to.hash }),
      },
      {
        name: 'security',
        path: 'system/security',
        alias: '/security',
        redirect: { name: 'profile', query: { tab: 'security' } },
        meta: {},
      },
      {
        name: 'system-adc',
        path: 'system/adc',
        component: () => import('../pages/adc/AdcPage.vue'),
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'system-users',
        path: 'system/users',
        component: () => import('../pages/system-users/SystemUsersPage.vue'),
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'system-permissions',
        path: 'system/roles',
        alias: '/system/permissions',
        component: () => import('../pages/system-permissions/SystemPermissionsPage.vue'),
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'users-legacy',
        path: 'users',
        redirect: { name: 'users' },
        // Legacy compatibility string kept for router smoke tests:
        // redirect: { name: 'system-users' }
      },
      {
        name: 'users',
        path: 'rbac/users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { requiredPermissions: ['user.view'] },
      },
      {
        name: 'projects-legacy',
        path: 'projects',
        redirect: { name: 'projects' },
      },
      {
        name: 'projects',
        path: 'rbac/projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
        meta: { requiredPermissions: ['project.view'] },
      },
      {
        name: 'permissions-legacy',
        path: 'permissions',
        redirect: { name: 'permissions' },
      },
      {
        name: 'permissions',
        path: 'rbac/permissions',
        component: () => import('../pages/roles/RolesPage.vue'),
        meta: { requiredPermissions: ['permission.view'] },
      },
      {
        name: 'secrets-legacy',
        path: 'secrets',
        redirect: { name: 'secrets' },
      },
      {
        name: 'secrets',
        path: 'rbac/secrets',
        component: () => import('../views/admin/api-keys/ApiKeyList.vue'),
        meta: { requiredPermissions: ['secret.view'] },
      },
      {
        name: 'operations-index',
        path: 'operations/index',
        redirect: { name: 'system-settings', query: { tab: 'index' } },
        meta: { requiredPermissions: ['project.view'] },
      },
      {
        name: 'operations-ai-first',
        path: 'operations/ai-first',
        component: () => import('../pages/operations/AiFirstPage.vue'),
        meta: { requiredPermissions: ['project.view'] },
      },
      {
        name: 'operations-graph',
        path: 'operations/graph',
        component: () => import('../pages/operations/GraphPage.vue'),
        meta: { requiredPermissions: ['project.view'], viewportLocked: true },
      },
      {
        name: 'security-guardian',
        path: 'system/guardian',
        alias: '/security/guardian',
        component: () => import('../pages/security/GuardianPage.vue'),
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'support-desk',
        path: 'support/desk',
        component: () => import('../pages/support/SupportDeskPage.vue'),
        meta: { requiredPermissions: ['support.chat.view', 'support.ticket.view'], viewportLocked: true, supportFocus: 'desk' },
      },
      {
        name: 'support-chat',
        path: 'support/chat',
        component: () => import('../pages/support/SupportDeskPage.vue'),
        meta: { requiredPermissions: ['support.chat.view'], viewportLocked: true, supportFocus: 'chat' },
      },
      {
        name: 'support-tickets',
        path: 'support/tickets',
        component: () => import('../pages/support/SupportDeskPage.vue'),
        meta: { requiredPermissions: ['support.ticket.view'], viewportLocked: true, supportFocus: 'tickets' },
      },
      {
        name: 'support-schedule',
        path: 'support/schedule',
        component: () => import('../pages/support/SupportSchedulePage.vue'),
        meta: { requiredPermissions: ['support.settings.manage'], viewportLocked: true },
      },
      {
        name: 'backup',
        path: 'system/backup',
        alias: '/backup',
        component: () => import('../pages/backup/BackupPage.vue'),
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'system-settings',
        path: 'system/settings',
        component: () => import('../pages/system-settings/SystemSettingsPage.vue'),
        meta: { requiredPermissions: ['project.view'], viewportLocked: true },
      },
      {
        name: 'llm-end',
        path: 'system/llm-end',
        alias: '/llm-end',
        redirect: { name: 'system-settings', query: { tab: 'llm' } },
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'llm-settings',
        path: 'system/llm-settings',
        alias: '/llm-settings',
        redirect: { name: 'system-settings', query: { tab: 'llm' } },
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'tts-settings',
        path: 'system/tts-settings',
        alias: '/tts-settings',
        redirect: { name: 'system-settings', query: { tab: 'tts' } },
        meta: { requiresAdminGroup: true },
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            redirect: { name: 'profile', query: { tab: 'billing', billing: 'payment-methods' } },
          },
          {
            name: 'billing',
            path: 'billing',
            redirect: { name: 'profile', query: { tab: 'billing', billing: 'billing' } },
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            redirect: { name: 'profile', query: { tab: 'billing', billing: 'pricing-plans' } },
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    alias: ['/system-users'],
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to) => {
  const isAuthRoute = String(to.path).startsWith('/auth')
  const isNotFoundRoute = to.name === '404'
  const canVisitAuthRouteWhenAuthenticated = new Set(['recover-password', 'recover-password-email'])
  let profile: Awaited<ReturnType<typeof fetchPanelProfile>> | null = null

  if (!isAuthRoute && !isNotFoundRoute && !isAdminAuthenticated()) {
    return { name: 'login' }
  }

  const queryClient = String(to.query.client ?? '').toLowerCase()
  const isRdaClientLogin = to.name === 'login' && queryClient === 'rda'
  // A one-time Google OAuth callback (`google_code`/`google_status`) must reach
  // the Login page even when a stale/expired local admin session is still
  // present, otherwise the guard below would bounce the navigation to
  // `/dashboard` and permanently drop the single-use code before it could be
  // exchanged via `POST /api/auth/google/complete`.
  const hasPendingGoogleLogin =
    to.name === 'login' &&
    (String(to.query.google_code ?? '').trim().length > 0 || String(to.query.google_status ?? '').trim().length > 0)
  const requiresAdminGroup = to.matched.some((record) => Boolean(record.meta?.requiresAdminGroup))

  // Validate persisted auth before protected page mounts so components
  // do not fire multiple unauthorized API requests in parallel.
  if (!isAuthRoute && !isNotFoundRoute && isAdminAuthenticated()) {
    try {
      profile = await fetchPanelProfile()
    } catch (error) {
      if (getHttpStatusCode(error) === 401) {
        expireAdminSession()
        return { name: 'login' }
      }

      // Keep any saved admin key intact if profile validation fails.
      return requiresAdminGroup ? { name: 'dashboard' } : true
    }
  }

  if (
    isAuthRoute &&
    isAdminAuthenticated() &&
    !canVisitAuthRouteWhenAuthenticated.has(String(to.name ?? '')) &&
    !isRdaClientLogin &&
    !hasPendingGoogleLogin
  ) {
    return { name: 'dashboard' }
  }

  if (requiresAdminGroup) {
    try {
      if (!profile) {
        profile = await fetchPanelProfile()
      }
      if (!profile?.is_system_admin) {
        return { name: 'dashboard' }
      }
    } catch (error) {
      if (getHttpStatusCode(error) === 401) {
        expireAdminSession()
        return { name: 'login' }
      }

      return { name: 'dashboard' }
    }
  }

  const requiredPermissions = ((to.meta?.requiredPermissions as string[] | undefined) ?? []).filter(Boolean)
  if (requiredPermissions.length === 0) {
    return true
  }

  // A system admin bypasses the permission list, matching the sidebar.
  //
  // The sidebar skips permission filtering entirely when `is_system_admin` is
  // true, so without this the two disagree: the menu item renders, but
  // navigating to it is bounced to the dashboard and the page looks broken
  // rather than forbidden. The admin group is defined by being a system admin,
  // not by holding a particular permission, so demanding the permission here
  // was the inconsistency.
  if (profile?.is_system_admin) {
    return true
  }

  const { hasAnyPermission } = usePermissions()
  if (hasAnyPermission(requiredPermissions)) {
    return true
  }

  return { name: 'dashboard' }
})

export default router
